import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useTenant } from "../../../Contexts/TenantContext";
import { useAuth } from "../../../Contexts/AuthContext";
import { useCart } from "../../../Contexts/CartContext";
import { productService } from "../../../Api/Services/productService";
import { cartShareService } from "../../../Api/Services/cartShareService";
import { shippingService } from "../../../Api/Services/shippingService";
import { orderService } from "../../../Api/Services/orderService";
import { couponService } from "../../../Api/Services/couponService";
import { tenantBranchService } from "../../../Api/Services/tenantBranchService";
import { TenantBranch } from "../../../Models/TenantBranch";
import { paymentService } from "../../../Api/Services/paymentService";
import { createOrderHubConnection } from "../../../Api/orderHub";
import { CreateOrderResponse } from "../../../Models/Order";
import { ShippingOptionResponse } from "../../../Models/Shipping";
import { BankingBilletChargeResponse, PixChargeResponse } from "../../../Models/Payment";
import { ShippingMethod } from "../../../Enums/ShippingMethod";
import { PaymentMethod } from "../../../Enums/PaymentMethod";
import { PaymentStatus } from "../../../Enums/PaymentStatus";
import { CreditCardData } from "../../../Types/CreditCardDataType";
import { formatPriceBRL, formatPostalCode } from "../../../Utils/DataFormatters";
import { getShippingMethodLabel } from "../../../Utils/Enums";
import { generatePaymentToken } from "../../../Utils/efiBankPayment";
import { needsPaymentSync, PaymentSyncSnapshot, syncPaymentMethod } from "../../../Utils/paymentSync";
import { trackGoogleAdsOrderCreatedSignal } from "../../../Utils/googleAdsTracking";
import { FaCheckCircle, FaCreditCard, FaRegEye, FaShippingFast, FaShoppingCart, FaTimes, FaTruck } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import ProgressBar from "../../../Components/ProgressBar/ProgressBar";
import CartStep from "../../../Components/CartStep/CartStep";
import DeliveryStep from "../../../Components/DeliveryStep/DeliveryStep";
import PaymentStep from "../../../Components/PaymentStep/PaymentStep";
import ReviewStep from "../../../Components/ReviewStep/ReviewStep";
import OrderSummary from "../../../Components/OrderSummary/OrderSummary";
import OrderCompletedStep from "../../../Components/OrderCompletedStep/OrderCompletedStep";
import OrderCreatedStep from "../../../Components/OrderCreatedStep/OrderCreatedStep";
import PixPaymentStep from "../../../Components/PixPaymentStep/PixPaymentStep";
import BankingBilletStep from "../../../Components/BankingBilletStep/BankingBilletStep";
import CartAuthModal from "../../../Components/CartAuthModal/CartAuthModal";
import CouponInput from "../../../Components/CouponInput/CouponInput";
import OrderNavigation from "../../../Components/OrderNavigation/OrderNavigation";
import FooterPolicies from "../../../Components/FooterPolicies/FooterPolicies";
import FooterInfo from "../../../Components/FooterInfo/FooterInfo";
import LoadingOverlay from "../../../Components/LoadingOverlay/LoadingOverlay";
import Button from "../../../Components/Button/Button";

const UserCartPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { tenantContact, tenantPreference, tenantShippingProviderConfig } = useTenant();
  const [branches, setBranches] = useState<TenantBranch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const { user, loading: authLoading } = useAuth();
  const { cartItems, getTotal, clearCart, addToCart } = useCart();

  const [loading, setLoading] = useState<boolean>(false);
  const [order, setOrder] = useState<CreateOrderResponse | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isImportingFromUrl, setIsImportingFromUrl] = useState(() => {
    const c = searchParams.get('c');
    if (c) {
      return !sessionStorage.getItem(`cart-import:c:${c}`);
    }
    const p = searchParams.get('p');
    if (p) {
      return !sessionStorage.getItem(`cart-import:${p}`);
    }
    return false;
  });

  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [couponDiscountPercentage, setCouponDiscountPercentage] = useState<number | null>(null);

  const [deliveryMode, setDeliveryMode] = useState<"store" | "address">("store");
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [selectedAddressText, setSelectedAddressText] = useState<string | null>(null);
  const [selectedShipping, setSelectedShipping] = useState<ShippingOptionResponse | null>(null);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);

  const [creditCardData, setCreditCardData] = useState<CreditCardData>({
    cardNumber: "",
    cardName: "",
    cardCvv: "",
    cardExpiration: "",
    holderDocument: "",
    selectedInstallments: 1,
    cardBrand: null
  });
  const [pixPaymentResult, setPixPaymentResult] = useState<PixChargeResponse | null>(null);
  const [bankingBilletResult, setBankingBilletResult] = useState<BankingBilletChargeResponse | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState<boolean>(false);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);
  const [orderPaymentSnapshot, setOrderPaymentSnapshot] = useState<PaymentSyncSnapshot | null>(null);

  const [cepValue, setCepValue] = useState("");
  const [cepShippingOptions, setCepShippingOptions] = useState<ShippingOptionResponse[] | null>(null);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [cepError, setCepError] = useState("");
  const [couponError, setCouponError] = useState("");

  const hubRef = useRef<ReturnType<typeof createOrderHubConnection> | null>(null);
  const orderIdRef = useRef<string | null>(null);
  const finalStepIndexRef = useRef(0);
  const paymentMethodRef = useRef<PaymentMethod | null>(null);
  const hasAppliedReturnStateRef = useRef(false);

  useEffect(() => {
    const state = location.state as { newAddressId?: string; returnStep?: number } | null;
    const newAddressId = state?.newAddressId;
    if (!newAddressId || hasAppliedReturnStateRef.current) {
      return;
    }

    hasAppliedReturnStateRef.current = true;
    setDeliveryMode("address");
    setSelectedAddressId(newAddressId);
    setSelectedAddressText(null);
    setSelectedShipping(null);
    const targetStep = typeof state.returnStep === "number" ? state.returnStep : 1;
    setCurrentStep(targetStep);
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const c = searchParams.get('c');
    if (!c) {
      return;
    }

    const sessionKey = `cart-import:c:${c}`;
    if (sessionStorage.getItem(sessionKey)) {
      setIsImportingFromUrl(false);
      return;
    }

    cartShareService.getItems(c).then(({ items }) => {
      const alreadyInCart = new Set(cartItems.map(i => i.id));
      const newItems = items.filter(item => !alreadyInCart.has(item.productId));

      if (newItems.length === 0) {
        sessionStorage.setItem(sessionKey, '1');
        setIsImportingFromUrl(false);
        return;
      }

      productService.getByIds(newItems.map(i => i.productId)).then(products => {
        products.forEach(product => {
          const qty = newItems.find(i => i.productId === product.id)?.quantity ?? 1;
          addToCart({
            id: product.id,
            name: product.name,
            code: product.code,
            price: product.price,
            quantity: qty,
            stockQuantity: product.stockQuantity,
            origin: product.origin,
            isMadeToOrder: product.isMadeToOrder,
            image: product.mainImageUrl
          }, { silent: true });
        });
        sessionStorage.setItem(sessionKey, '1');
      }).catch(() => {
        // silent on error
      }).finally(() => {
        setIsImportingFromUrl(false);
      });
    }).catch(() => {
      setIsImportingFromUrl(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const p = searchParams.get('p');
    if (!p) {
      return;
    }

    // Guard against re-importing the same param set within the same browser session
    // (e.g. user removes an item then refreshes — we don't want to silently re-add it)
    const sessionKey = `cart-import:${p}`;
    if (sessionStorage.getItem(sessionKey)) {
      return;
    }

    const entries = p.split(',').map(entry => {
      const [id, qtyStr] = entry.split(':');
      const qty = qtyStr ? parseInt(qtyStr, 10) : 1;
      return { id: id.trim(), qty: isNaN(qty) || qty < 1 ? 1 : qty };
    }).filter(e => e.id);

    const uniqueIds = [...new Set(entries.map(e => e.id))];

    const qtyMap = new Map<string, number>();
    entries.forEach(e => {
      if (!qtyMap.has(e.id)) {
        qtyMap.set(e.id, e.qty);
      }
    });

    const alreadyInCart = new Set(cartItems.map(i => i.id));
    const newIds = uniqueIds.filter(id => !alreadyInCart.has(id));

    if (newIds.length === 0) {
      sessionStorage.setItem(sessionKey, '1');
      setIsImportingFromUrl(false);
      return;
    }

    productService.getByIds(newIds).then(products => {
      products.forEach(product => {
        const qty = qtyMap.get(product.id) ?? 1;
        addToCart({
          id: product.id,
          name: product.name,
          code: product.code,
          price: product.price,
          quantity: qty,
          stockQuantity: product.stockQuantity,
          origin: product.origin,
          isMadeToOrder: product.isMadeToOrder,
          image: product.mainImageUrl
        }, { silent: true });
      });
      sessionStorage.setItem(sessionKey, '1');
    }).catch(() => {
      // silent on error
    }).finally(() => {
      setIsImportingFromUrl(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetCreditCardData = () => {
    setCreditCardData({
      cardNumber: "",
      cardName: "",
      cardCvv: "",
      cardExpiration: "",
      holderDocument: "",
      selectedInstallments: 1,
      cardBrand: null
    });
  };

  const bankingBilletFeeValue = tenantPreference?.bankingBilletFee ?? 0;
  const hasMadeToOrderItems = cartItems.some(item => item.isMadeToOrder && item.stockQuantity < item.quantity);
  const allowStorePickup = tenantShippingProviderConfig?.offerStorePickup ?? false;

  useEffect(() => {
    if (!allowStorePickup) {
      return;
    }
    tenantBranchService.getAll().then(setBranches).catch(() => {});
  }, [allowStorePickup]);
  
  const bankingBilletFee = useMemo(() => {
    if (selectedPaymentMethod === PaymentMethod.BankingBillet) {
      return bankingBilletFeeValue;
    }

    return 0;
  }, [bankingBilletFeeValue, selectedPaymentMethod]);

  useEffect(() => {
    if (!allowStorePickup && deliveryMode === "store") {
      setDeliveryMode("address");
      setSelectedShipping(null);
    }
  }, [allowStorePickup, deliveryMode]);

  useEffect(() => {
    if (!authLoading && !user && currentStep > 0) {
      setCurrentStep(0);
      setSelectedShipping(null);
      setSelectedAddressId(null);
      setSelectedAddressText(null);
    }
  }, [authLoading, user, currentStep]);

  const [steps, setSteps] = useState([
    { label: "Carrinho", icon: <FaShoppingCart /> },
    { label: "Entrega", icon: <FaTruck /> }
  ]);

  const total = getTotal();
  const couponDiscountAmount = couponDiscountPercentage
    ? (total * couponDiscountPercentage) / 100
    : 0;

  const shippingAllowsImmediatePayment = (shipping: ShippingOptionResponse | null) => {
    if (!shipping) {
      return false;
    }

    return [
      ShippingMethod.CorreiosPAC, 
      ShippingMethod.CorreiosSedex, 
      ShippingMethod.FreeShipping, 
      ShippingMethod.StorePickup
    ].includes(shipping.methodType);
  };

  const isImmediatePayment = !hasMadeToOrderItems && shippingAllowsImmediatePayment(selectedShipping);
  const isAwaitingConfirmation =
    !isPaymentConfirmed &&
    (selectedPaymentMethod === PaymentMethod.Pix ||
      selectedPaymentMethod === PaymentMethod.BankingBillet);
  const hasMadeToOrder = order?.hasMadeToOrderItems ?? hasMadeToOrderItems;
  const orderShippingMethod = order?.shippingMethod ?? selectedShipping?.methodType ?? null;

  const updateStepsForPaymentMethod = (method: PaymentMethod | null): void => {
    if (!selectedShipping) {
      return;
    }

    if (!isImmediatePayment) {
      setSteps(getSteps(selectedShipping.methodType, false));
      return;
    }

    const base = getSteps(selectedShipping.methodType, true);

    // method null/undefined => just keep the base steps
    if (method == null) {
      setSteps(base);
      return;
    }

    if (method === PaymentMethod.Pix) {
      setSteps([
        ...base.slice(0, 3),
        { label: "PIX", icon: <FaRegEye /> },
        { label: "Concluir", icon: <FaCheckCircle /> }
      ]);
      return;
    }

    if (method === PaymentMethod.BankingBillet) {
      setSteps([
        ...base.slice(0, 3),
        { label: "Boleto", icon: <FaRegEye /> },
        { label: "Concluir", icon: <FaCheckCircle /> }
      ]);
      return;
    }

    // Credit card and others
    setSteps([
      ...base.slice(0, 3),
      { label: "Concluir", icon: <FaCheckCircle /> }
    ]);
  };

  const nextStep = async () => {
    if (currentStep === 0) {
      const minOrder = tenantPreference?.minOrderSubtotal;
      if (minOrder && getTotal() < minOrder) {
        toast.warn(`${"Valor mínimo para finalizar a compra"}: ${formatPriceBRL(minOrder)}.`);
        return;
      }
      if (!user) {
        setShowAuthModal(true);
        return;
      }
    }

    if (currentStep === 1) {
      if (deliveryMode === "store") {
        if (branches.length > 0 && !selectedBranchId) {
          toast.info("Selecione um local de retirada para continuar.");
          return;
        }
        if (!selectedShipping) {
          toast.info("Selecione uma opção de retirada para continuar.");
          return;
        }
      }

      if (deliveryMode === "address") {
        if (!selectedAddressId) {
          toast.info("Selecione um endereço para continuar.");
          return;
        }

        if (!selectedShipping) {
          toast.info("Selecione uma opção de entrega para continuar.");
          return;
        }
      }
    }

    if (currentStep === 2) {
      setLoading(true);

      if (isImmediatePayment) {
        if (selectedPaymentMethod === null) {
          toast.info("Selecione um método de pagamento para continuar.");
          setLoading(false);
          return;
        }

        if (selectedPaymentMethod === PaymentMethod.CreditCard) {
          const { cardNumber, cardName, cardCvv, cardExpiration, holderDocument } = creditCardData;

          if (!cardNumber || !cardName || !cardCvv || !cardExpiration || !holderDocument) {
            toast.error("Por favor, preencha todos os campos do cartão de crédito para continuar.");
            setLoading(false);
            return;
          }
        }
      }

      try {
        const currentOrder = order ?? await handleCreateOrder();
        if (!currentOrder) {
          return;
        }

        if (isImmediatePayment) {
          await handleCompleteOrder(currentOrder);
          return;
        }

        // Non-immediate payments: move to the final "order created" step
        setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
      } finally {
        if (!isImmediatePayment) {
          setLoading(false);
        }
      }
    }

    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const prevStep = () => {
    if (currentStep === 0) {
      navigate("/");
    } else {
      setCurrentStep((s) => Math.max(s - 1, 0));
    }
  };

  const getSteps = useCallback((shippingMethod: ShippingMethod | null, immediate: boolean) => {
    const baseSteps = [
      { label: "Carrinho", icon: <FaShoppingCart /> },
      { label: "Entrega", icon: <FaTruck /> }
    ];

    if (shippingMethod === null) {
      return baseSteps;
    }

    if (immediate) {
      return [
        ...baseSteps,
        { label: "Pagamento", icon: <FaCreditCard /> },
        { label: "Concluir", icon: <FaCheckCircle /> },
      ];
    }

    return [
      ...baseSteps,
      { label: "Confirmação", icon: <FaRegEye /> },
      { label: "Concluir", icon: <FaCheckCircle /> },
    ];
  }, []);

  useEffect(() => {
    // When an order already exists (final step), keep the current steps
    if (order) {
      return;
    }

    setSteps(getSteps(selectedShipping?.methodType ?? null, isImmediatePayment));
  }, [getSteps, selectedShipping?.methodType, isImmediatePayment, order]);

  useEffect(() => {
    orderIdRef.current = order?.id ?? null;
  }, [order?.id]);

  useEffect(() => {
    finalStepIndexRef.current = steps.length - 1;
  }, [steps.length]);

  useEffect(() => {
    paymentMethodRef.current = selectedPaymentMethod;
  }, [selectedPaymentMethod]);

  useEffect(() => {
    if (!order?.id) {
      return;
    }

    const isFinalStep = currentStep === steps.length - 1;
    const isOrderCreatedStep = isFinalStep && !isImmediatePayment;

    if (!isOrderCreatedStep) {
      return;
    }

    trackGoogleAdsOrderCreatedSignal({
      config: {
        googleAdsConversionId: tenantPreference?.googleAdsConversionId ?? null
      },
      transactionId: order.id,
      value: Math.max(0, getTotal() - couponDiscountAmount + (selectedShipping?.price ?? 0)),
      currencyCode: "BRL"
    });
  }, [
    couponDiscountAmount,
    currentStep,
    getTotal,
    isImmediatePayment,
    order?.id,
    selectedPaymentMethod,
    selectedShipping?.price,
    steps.length,
    tenantPreference?.googleAdsConversionId
  ]);

  useEffect(() => {
    if (hubRef.current) {
      return;
    }

    const hub = createOrderHubConnection();
    hubRef.current = hub;

    const handleStatusChanged = (payload: { orderId?: string; status?: string }) => {
      if (!payload?.orderId || payload.orderId !== orderIdRef.current) {
        return;
      }

      if (payload.status === "Paid" || payload.status === "Shipped") {
        const method = paymentMethodRef.current;
        if (
          method === PaymentMethod.Pix ||
          method === PaymentMethod.BankingBillet
        ) {
          setIsPaymentConfirmed(true);
          setIsProcessingPayment(false);
          setCurrentStep(finalStepIndexRef.current);
        }
      }
    };

    hub.on("OrderStatusChanged", handleStatusChanged);

    hub.start().catch(() => {
      // Ignore hub start errors; manual refresh still works
    });

    return () => {
      hub.off("OrderStatusChanged", handleStatusChanged);
      hub.stop();
      hubRef.current = null;
    };
  }, []);

  const handleSelectShipping = (shipping: ShippingOptionResponse | null) => {
    setSelectedShipping(shipping);
    const immediate = !hasMadeToOrderItems && shippingAllowsImmediatePayment(shipping);
    setSteps(getSteps(shipping?.methodType ?? null, immediate));
  }

  const handleCreateOrder = async (): Promise<CreateOrderResponse | null> => {
    if (selectedShipping == null) {
      return null;
    }

    const needsAddress = selectedShipping.methodType !== ShippingMethod.StorePickup;
    if (needsAddress && !selectedAddressId) {
      return null;
    }

    try {
      const orderResponse = await orderService.create({
        addressId: needsAddress ? selectedAddressId : null,
        pickedUpAtBranchId: !needsAddress ? (selectedBranchId && selectedBranchId !== "tenant_contact" ? selectedBranchId : null) : null,
        shippingMethod: selectedShipping.methodType,
        shippingPrice: selectedShipping.price,
        deliveryTime: selectedShipping.deliveryTime,
        paymentMethod: selectedPaymentMethod,
        products: cartItems.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          size: item.size ?? null
        })),
        couponCode: appliedCoupon ?? null,
        couponDiscountPercentage: couponDiscountPercentage ?? null,
        creditCardInstallments: selectedPaymentMethod === PaymentMethod.CreditCard ? creditCardData.selectedInstallments : null
      });

      setOrder(orderResponse);
      setOrderPaymentSnapshot({
        paymentMethod: orderResponse?.paymentMethod ?? null,
        creditCardInstallments: orderResponse?.creditCardInstallments ?? null
      });

      clearCart();

      if (!isImmediatePayment) {
        setSelectedAddressId(null);
        setSelectedAddressText(null);
        setSelectedShipping(null);
      }

      if (!isImmediatePayment) {
        setCurrentStep((_) => Math.max(steps.length - 1, 0));
      }

      return orderResponse;
    } catch (error) {
      toast.error("Não foi possível criar o pedido, tente novamente mais tarde.");
      return null;
    }
  };

  const handleCompleteOrder = async (
    currentOrder: CreateOrderResponse | null = order
  ): Promise<void> => {
    if (selectedPaymentMethod == null || !currentOrder) {
      return;
    }

    const method = selectedPaymentMethod as PaymentMethod;
    const finalStepIndex = steps.length - 1;
    const paymentStepIndex = steps.length - 2;

    try {
      setLoading(true);
      const targetSnapshot: PaymentSyncSnapshot = {
        paymentMethod: method,
        creditCardInstallments: method === PaymentMethod.CreditCard ? creditCardData.selectedInstallments ?? null : null
      };
      const currentSnapshot: PaymentSyncSnapshot = orderPaymentSnapshot ?? {
        paymentMethod: currentOrder.paymentMethod ?? null,
        creditCardInstallments: currentOrder.creditCardInstallments ?? null
      };
      const shouldSync = needsPaymentSync(currentSnapshot, targetSnapshot);

      if (shouldSync) {
        const synced = await syncPaymentMethod({
          orderId: currentOrder.id,
          paymentMethod: method,
          creditCardInstallments: targetSnapshot.creditCardInstallments ?? null
        });

        if (!synced) {
          toast.error("Não foi possível atualizar o método de pagamento. Tente novamente.");
          setLoading(false);
          return;
        }

        setOrderPaymentSnapshot(targetSnapshot);
      }

      switch (method) {
        case PaymentMethod.CreditCard: {
          const result = await generatePaymentToken(creditCardData);

          if (!result) {
            toast.error("Não foi possível realizar o pagamento, tente novamente mais tarde.");
            setLoading(false);
            return;
          }

          const { paymentToken, cardMask } = result;

          if (paymentToken) {
            const result = await paymentService.createCreditCardCharge({
              orderId: currentOrder.id,
              paymentToken: paymentToken,
              installments: creditCardData.selectedInstallments!,
              maskedCardNumber: cardMask
            });

            if (!result) {
              toast.error("Não foi possível processar o pagamento. Tente novamente.");
              setCurrentStep(paymentStepIndex);
              return;
            }

            if (result.status === PaymentStatus.Paid) {
              setIsProcessingPayment(false);
              setIsPaymentConfirmed(true);
              setCurrentStep(finalStepIndex);
            } else if (result.status === PaymentStatus.Processing) {
              setIsProcessingPayment(true);
              setCurrentStep(finalStepIndex);
            } else {
              // Retry if allowed
              if (result.retryable && result.chargeId) {
                const retryResult = await paymentService.retryCreditCardCharge({
                  chargeId: result.chargeId,
                  orderId: currentOrder.id,
                  paymentToken,
                  installments: creditCardData.selectedInstallments!,
                  maskedCardNumber: cardMask
                });

                if (retryResult) {
                  if (retryResult.status === PaymentStatus.Paid) {
                    setIsProcessingPayment(false);
                    setIsPaymentConfirmed(true);
                    setCurrentStep(finalStepIndex);
                    return;
                  }
                  if (retryResult.status === PaymentStatus.Processing) {
                    setIsProcessingPayment(true);
                    setCurrentStep(finalStepIndex);
                    return;
                  }
                  const reason = retryResult.refusalReason ?? result.refusalReason ?? "Pagamento não autorizado. Tente novamente ou escolha outro método.";
                  toast.error(reason);
                } else {
                  const reason = result.refusalReason ?? "Pagamento não autorizado. Tente novamente ou escolha outro método.";
                  toast.error(reason);
                }
              } else {
                const reason = result.refusalReason ?? "Pagamento não autorizado. Tente novamente ou escolha outro método.";
                toast.error(reason);
              }
              setCurrentStep(paymentStepIndex);
            }
          }
          return;
        }

        case PaymentMethod.Pix: {
          const pixResult = await paymentService.createPixCharge({ orderId: currentOrder.id });
          setPixPaymentResult(pixResult);
          setCurrentStep(paymentStepIndex);
          return;
        }

        case PaymentMethod.BankingBillet: {
          const billetResult = await paymentService.createBankingBilletCharge({ orderId: currentOrder.id });   
          setBankingBilletResult(billetResult);
          setCurrentStep(paymentStepIndex);
          return;
        }

        default:
          return;
      }
    } catch (error) {
      toast.error("Não foi possível completar o pedido, tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async (code: string) => {
    if (!code) {
      setCouponError("Informe um código de cupom.");
      setTimeout(() => setCouponError(""), 3000);
      return;
    }

    try {
      const coupon = await couponService.validate(code);

      setCouponDiscountPercentage(coupon.discountPercentage);
      setAppliedCoupon(coupon.code);
      toast.success(`${"Cupom"} "${coupon.code}" ${"aplicado"}!`);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setCouponError("Não foi encontrado cupom ativo com esse código.");
      } else {
        setCouponError("Não foi possível aplicar o cupom de desconto, tente novamente mais tarde.");
      }
      setTimeout(() => setCouponError(""), 3000);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponDiscountPercentage(null);
    setAppliedCoupon(null);
    toast.success("Cupom removido!");
  };

  const handleCepCalculate = async () => {
    const digits = cepValue.replace(/\D/g, "");
    if (digits.length !== 8) {
      setCepError("Informe um CEP válido com 8 dígitos.");
      setTimeout(() => setCepError(""), 3000);
      return;
    }
    setIsCepLoading(true);
    try {
      const options = await shippingService.getOptions({
        products: cartItems.map(item => ({ productId: item.id, quantity: item.quantity })),
        destinationZip: digits
      });
      setCepShippingOptions(options);
      if (!options || options.length === 0) {
        toast.info("Nenhuma opção de entrega encontrada para este CEP.");
      }
    } catch {
      toast.error("Não foi possível calcular o frete. Tente novamente.");
    } finally {
      setIsCepLoading(false);
    }
  };

  const handlePaymentMethodChange = async (method: PaymentMethod) => {
    setSelectedPaymentMethod(method);
    setPixPaymentResult(null);
    setBankingBilletResult(null);
    if (method !== PaymentMethod.CreditCard) {
      resetCreditCardData();
    }

    if (isImmediatePayment) {
      updateStepsForPaymentMethod(method);
    }
  };

  const isFinalStep = currentStep === steps.length - 1;
  const isOrderFinalStep = isFinalStep && order !== null;
  const shouldShowSidebar = !isOrderFinalStep || order === null;
  const getCreditCardInterest = () => {
    if (selectedPaymentMethod !== PaymentMethod.CreditCard || !creditCardData.selectedInstallments) {
      return 0;
    }

    const maxNoInterest = tenantPreference?.cardMaxNoInterestInstallments ?? 0;
    const installments = creditCardData.selectedInstallments;
    if (installments <= 1 || installments <= maxNoInterest) {
      return 0;
    }

    const basePrice = getTotal() - couponDiscountAmount + (selectedShipping?.price ?? 0);
    const monthlyRate = 0.0129;
    const totalRate = monthlyRate * (installments - 1);
    return basePrice * totalRate;
  };
  const creditCardInterest = getCreditCardInterest();

  return (
    <div className={`min-h-screen flex flex-col overflow-x-hidden relative bg-gray-50`}>
      <SmartNavbar />
      {loading && <LoadingOverlay topOffset="112px" />}
      <main className={`w-full flex flex-col items-center flex-1 min-h-0 py-10`}>
        {cartItems.length > 0 || order !== null || currentStep > 0 || isImportingFromUrl ? (
          <div className={`w-full flex flex-col ${isOrderFinalStep ? "flex-1" : "gap-9"}`}>
            {user ? (
              <ProgressBar steps={steps} currentStep={currentStep} />
            ) : (
              <div className="page-gutter">
                <PageTitle className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  CARRINHO
                  <span className="text-sm font-normal normal-case text-gray-500">
                    Finalize a compra para fazer o pedido.
                  </span>
                </PageTitle>
              </div>
            )}

            <div className={`flex flex-col ${isOrderFinalStep ? "flex-1 items-center justify-center page-gutter" : "lg:flex-row gap-7 page-gutter"}`}>
              {currentStep === 0 && <CartStep />}
              {currentStep === 1 && (
                <DeliveryStep
                  mode={deliveryMode}
                  onModeChange={(m) => { setDeliveryMode(m); setSelectedBranchId(null); }}
                  allowStorePickup={allowStorePickup}
                  selectedShipping={selectedShipping}
                  onSelectShipping={handleSelectShipping}
                  selectedAddressId={selectedAddressId}
                  onSelectAddress={(id, formatted) => {
                    setSelectedAddressId(id);
                    setSelectedAddressText(formatted);
                  }}
                  cartItems={cartItems}
                  tenantContact={tenantContact}
                  branches={branches}
                  selectedBranchId={selectedBranchId}
                  onSelectBranch={setSelectedBranchId}
                />
              )}
              {currentStep === 2 && (
                isImmediatePayment ? (
                  <PaymentStep
                    selectedPaymentMethod={selectedPaymentMethod}
                    onSelectPaymentMethod={handlePaymentMethodChange}
                    creditCardData={creditCardData}
                    onCreditCardDataChange={setCreditCardData}
                    orderPrice={
                      getTotal() -
                      couponDiscountAmount +
                      (selectedShipping?.price ?? 0) +
                      (selectedPaymentMethod === PaymentMethod.BankingBillet ? bankingBilletFee : 0)
                    }
                  />
                ) : (
                  <ReviewStep
                    cartItems={cartItems}
                    selectedAddress={selectedAddressText}
                    selectedShipping={selectedShipping}
                  />
                )
              )}
              {currentStep === steps.length - 2 && selectedPaymentMethod === PaymentMethod.Pix && pixPaymentResult && (
                <PixPaymentStep pixData={pixPaymentResult} />
              )}
              {currentStep === steps.length - 2 && selectedPaymentMethod === PaymentMethod.BankingBillet && bankingBilletResult && (
                <BankingBilletStep
                  billetUrl={bankingBilletResult.billetLink}
                  billetPdf={bankingBilletResult.billetPdf}
                  expiration={bankingBilletResult.expiration}
                />
              )}

              {currentStep === steps.length - 1 && order && (
                isImmediatePayment &&
                selectedPaymentMethod != null
                  ? (
                    <OrderCompletedStep
                      orderNumber={order.orderNumber}
                      awaitingConfirmation={isAwaitingConfirmation}
                      processingPayment={isProcessingPayment}
                      isStorePickup={orderShippingMethod === ShippingMethod.StorePickup}
                      pickupDays={tenantShippingProviderConfig?.storePickupEstimatedTimeInDays}
                    />
                  ) : (
                    <OrderCreatedStep
                      orderNumber={order.orderNumber}
                      hasMadeToOrderItems={hasMadeToOrder}
                      shippingMethod={orderShippingMethod}
                    />
                  )
              )}

              {shouldShowSidebar && (() => {
                const isPaymentCreatedStep =
                  currentStep === steps.length - 2 &&
                  (selectedPaymentMethod === PaymentMethod.Pix ||
                    selectedPaymentMethod === PaymentMethod.BankingBillet);

                return (
                  <div className="flex flex-col gap-4 lg:w-[340px] xl:w-[380px] lg:shrink-0">
                    {!isPaymentCreatedStep && (
                      <>
                        {(() => {
                          const isCreditCard = selectedPaymentMethod === PaymentMethod.CreditCard;
                          const surchargePercent = (isCreditCard && tenantPreference?.enableCreditCardSurcharge)
                            ? (tenantPreference.creditCardSurchargePercent ?? 0)
                            : 0;
                          const surchargeBase = getTotal() - couponDiscountAmount + (selectedShipping?.price ?? 0);
                          const surchargeAmount = surchargePercent > 0
                            ? Math.round(surchargeBase * surchargePercent) / 100
                            : 0;
                          return (
                            <OrderSummary
                              getTotalCartItems={getTotal}
                              orderCouponDiscount={couponDiscountAmount ?? 0}
                              orderShippingPrice={selectedShipping?.price ?? null}
                              orderShippingMethod={selectedShipping?.methodType ?? null}
                              orderBankingBilletFee={selectedPaymentMethod === PaymentMethod.BankingBillet ? bankingBilletFee : 0}
                              orderBaseBankingBilletFee={tenantPreference?.bankingBilletFee ?? null}
                              orderCreditCardSurcharge={surchargeAmount > 0 ? surchargeAmount : null}
                              orderInterest={creditCardInterest}
                              orderFinalPrice={
                                surchargeBase +
                                (selectedPaymentMethod === PaymentMethod.BankingBillet ? bankingBilletFee : 0) +
                                surchargeAmount +
                                (isCreditCard ? creditCardInterest : 0)
                              }
                            />
                          );
                        })()}
                        {currentStep === 0 && (
                          // Side by side only while the summary spans the full width (768px to 1020px)
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
                            {tenantShippingProviderConfig?.offerFreeShipping &&
                             tenantShippingProviderConfig?.minOrderSubtotalForFreeShipping &&
                             getTotal() >= tenantShippingProviderConfig.minOrderSubtotalForFreeShipping ? (
                              <div className="bg-white border rounded shadow p-4 flex items-center gap-3 text-sm text-green-800">
                                <FaShippingFast className="text-green-600 shrink-0" />
                                <span className="font-medium">Frete grátis disponível para este pedido!</span>
                              </div>
                            ) : (
                            <div className="bg-white border rounded shadow p-4 flex flex-col gap-1">
                              <h4 className="text-md font-semibold mb-3">{"Calcule o frete"}</h4>
                              <div className="flex gap-2">
                                <div className="relative flex-1">
                                  <input
                                    type="text"
                                    placeholder="CEP"
                                    value={formatPostalCode(cepValue)}
                                    onChange={e => {
                                      const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
                                      setCepValue(digits);
                                      if (digits.length === 0) {
                                        setCepShippingOptions(null);
                                      }
                                    }}
                                    onKeyDown={e => e.key === "Enter" && handleCepCalculate()}
                                    className="border rounded px-3 py-2 text-sm w-full focus:outline-none focus:ring-1 focus:ring-primary-600"
                                    maxLength={9}
                                  />
                                  {cepValue.length > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => { setCepValue(""); setCepShippingOptions(null); }}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                      <FaTimes size={12} />
                                    </button>
                                  )}
                                </div>
                                <Button
                                  onClick={handleCepCalculate}
                                  isLoading={isCepLoading}
                                  icon={<FaTruck className="text-xs shrink-0" />}
                                  className="px-3 py-2 text-sm flex items-center justify-center gap-1 w-[120px] shrink-0"
                                >
                                  {"Calcular"}
                                </Button>
                              </div>
                              {cepError && <p className="text-xs text-red-500">{cepError}</p>}
                              <a
                                href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary-600 hover:underline mt-1"
                              >
                                Não sei meu CEP
                              </a>
                              {cepShippingOptions && cepShippingOptions.length > 0 && (
                                <div className="flex flex-col gap-1 mt-1 border-t pt-2">
                                  {cepShippingOptions.map((opt, i) => (
                                    <div key={i} className="flex justify-between text-sm text-gray-700">
                                      <span>{getShippingMethodLabel(opt.methodType)}</span>
                                      <span className="font-medium">
                                        {opt.price === 0 ? "Grátis" : opt.price != null ? formatPriceBRL(opt.price) : "A definir"}
                                        {opt.deliveryTime != null && ` · ${opt.deliveryTime}d`}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            )}
                            <CouponInput
                              error={couponError}
                              appliedCoupon={appliedCoupon ?? undefined}
                              onApply={handleApplyCoupon}
                              onRemove={handleRemoveCoupon}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <OrderNavigation
                      isOrderStep={currentStep === 2}
                      onNext={nextStep}
                      onBack={prevStep}
                      hideBackButton={isPaymentCreatedStep}
                      nextLabel={currentStep === 0 ? (user ? "Continuar" : "Finalizar Compra") : undefined}
                      backLabel={currentStep === 0 ? "Continuar comprando" : undefined}
                    />
                  </div>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col justify-center items-center flex-1 text-center">
            <h3 className="mb-6 text-xl font-bold">O seu carrinho está vazio.</h3>
            <Link 
              to="/"
              className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white py-3 px-6 rounded hover:bg-primary-700"
            >
              <FaShoppingCart />
              Continuar comprando
            </Link>
          </div>
        )}
      </main>
      <FooterPolicies />
      <FooterInfo />
      <CartAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={() => setShowAuthModal(false)}
      />
    </div>
  );
};

export default UserCartPage;
